import { useState } from 'react'
import { Link } from 'react-router-dom'
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import {
  ArrowLeft,
  CreditCard,
  Plus,
  History,
  Zap,
  ShoppingCart,
  Gift,
  Check
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useCredits } from '../context/CreditContext'
import LoadingSpinner from '../components/LoadingSpinner'
import StripePaymentForm from '../components/StripePaymentForm'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')

const creditPackages = [
  { credits: 10, price: 9.99, popular: false },
  { credits: 50, price: 39.99, popular: true },
  { credits: 100, price: 69.99, popular: false },
  { credits: 500, price: 299.99, popular: false },
]

// Payment Modal Component
function PaymentModal({ 
  isOpen, 
  onClose, 
  credits, 
  price, 
  clientSecret,
  onSuccess
}: {
  isOpen: boolean
  onClose: () => void
  credits: number
  price: number
  clientSecret: string
  onSuccess: () => void
}) {
  const [error, setError] = useState('')

  if (!isOpen) return null

  const elementsOptions: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
    },
  }

  return (
    <div className="modal-overlay animate-fade-in">
      <div className="modal animate-slide-up max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              购买 {credits} 积分
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              ✕
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <Elements stripe={stripePromise} options={elementsOptions}>
            <StripePaymentForm
              credits={credits}
              price={price}
              clientSecret={clientSecret}
              onSuccess={() => {
                onSuccess()
                onClose()
                setError('')
              }}
              onError={setError}
            />
          </Elements>
        </div>
      </div>
    </div>
  )
}

export default function CreditsPage() {
  const [purchasing, setPurchasing] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [paymentModal, setPaymentModal] = useState<{
    isOpen: boolean
    credits: number
    price: number
    clientSecret: string
  }>({
    isOpen: false,
    credits: 0,
    price: 0,
    clientSecret: ''
  })

  // Redemption code states
  const [redemptionCode, setRedemptionCode] = useState('')
  const [redemptionLoading, setRedemptionLoading] = useState(false)
  const [redemptionSuccess, setRedemptionSuccess] = useState('')
  
  const { user, refreshUser } = useAuth()
  const { transactions, loading, purchaseCredits } = useCredits()

  const handlePurchase = async (credits: number, price: number) => {
    if (!user || purchasing) return

    setPurchasing(credits)
    setError('')

    try {
      const paymentIntent = await purchaseCredits(credits)
      if (!paymentIntent) {
        throw new Error('Failed to create payment intent')
      }

      // Open payment modal with client secret
      setPaymentModal({
        isOpen: true,
        credits,
        price,
        clientSecret: paymentIntent.clientSecret
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purchase failed')
    } finally {
      setPurchasing(null)
    }
  }

  const handlePaymentSuccess = async () => {
    // Refresh user credits and transactions
    await refreshUser()
    // Note: You might want to refresh transactions here as well
    setError('')
  }

  const handleRedeemCode = async () => {
    if (!redemptionCode.trim()) {
      setError('请输入兑换码')
      return
    }

    setRedemptionLoading(true)
    setError('')
    setRedemptionSuccess('')

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        throw new Error('请先登录')
      }

      const response = await fetch('/api/credits/redeem', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code: redemptionCode.trim()
        })
      })

      const result = await response.json()

      if (result.success) {
        setRedemptionSuccess(result.data.message)
        setRedemptionCode('')
        await refreshUser() // Refresh user credits
        // Note: You might want to refresh transactions here as well
      } else {
        setError(result.error || '兑换失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '兑换失败')
    } finally {
      setRedemptionLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">请登录后管理积分</p>
          <Link to="/login" className="btn-primary">
            立即登录
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回控制面板</span>
              </Link>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-xl font-semibold text-gray-900">积分管理</h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
              <Zap className="w-5 h-5 text-blue-600" />
              <span className="text-blue-600 font-medium text-lg">
                {user.credits} 积分
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Purchase Credits */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                购买积分
              </h2>
              <p className="text-gray-600">
                选择适合您需求的积分套餐
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {redemptionSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                {redemptionSuccess}
              </div>
            )}

            <div className="space-y-4">
              {creditPackages.map((pkg) => (
                <div
                  key={pkg.credits}
                  className={`relative bg-white rounded-lg border-2 p-6 ${
                    pkg.popular 
                      ? 'border-blue-500 shadow-lg' 
                      : 'border-gray-200 shadow-sm'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-4 bg-blue-500 text-white px-3 py-1 text-sm font-medium rounded-full">
                      推荐
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <Zap className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {pkg.credits} 积分
                        </h3>
                        <p className="text-sm text-gray-600">
                          约可生成 {pkg.credits} 张图片
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        ¥{pkg.price}
                      </div>
                      <div className="text-sm text-gray-500">
                        ¥{(pkg.price / pkg.credits).toFixed(2)}/积分
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handlePurchase(pkg.credits, pkg.price)}
                    disabled={purchasing !== null}
                    className={`w-full mt-4 flex justify-center items-center space-x-2 py-3 rounded-lg font-medium transition-colors ${
                      pkg.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {purchasing === pkg.credits ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        <span>立即购买</span>
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* Redemption Code Section */}
            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-orange-100 p-3 rounded-full">
                  <Gift className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    兑换码兑换
                  </h3>
                  <p className="text-sm text-gray-600">
                    输入兑换码获得免费积分
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <input
                  type="text"
                  value={redemptionCode}
                  onChange={(e) => setRedemptionCode(e.target.value.toUpperCase())}
                  placeholder="输入兑换码"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={redemptionLoading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleRedeemCode()
                    }
                  }}
                />
                <button
                  onClick={handleRedeemCode}
                  disabled={redemptionLoading || !redemptionCode.trim()}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {redemptionLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>兑换</span>
                    </>
                  )}
                </button>
              </div>

              <div className="mt-3 text-sm text-gray-500">
                兑换码由大小写字母和数字组成，不区分大小写
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">支付说明</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• 支持信用卡、借记卡等多种支付方式</li>
                <li>• 所有交易均通过 Stripe 安全处理</li>
                <li>• 积分购买后立即到账</li>
                <li>• 积分永久有效，无过期时间</li>
              </ul>
            </div>
          </div>

          {/* Transaction History */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                交易记录
              </h2>
              <p className="text-gray-600">
                查看您的积分使用和购买历史
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <History className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-900">最近交易</span>
                </div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="md" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">暂无交易记录</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex justify-between items-start py-3 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-full ${
                            transaction.type === 'purchase' 
                              ? 'bg-green-100' 
                              : transaction.type === 'usage'
                              ? 'bg-red-100'
                              : 'bg-blue-100'
                          }`}>
                            {transaction.type === 'purchase' ? (
                              <Plus className="w-4 h-4 text-green-600" />
                            ) : transaction.type === 'usage' ? (
                              <CreditCard className="w-4 h-4 text-red-600" />
                            ) : (
                              <Zap className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          
                          <div>
                            <p className="font-medium text-gray-900">
                              {transaction.description}
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(transaction.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className={`font-medium ${
                            transaction.amount > 0 
                              ? 'text-green-600' 
                              : 'text-red-600'
                          }`}>
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                          </div>
                          <div className="text-xs text-gray-500">
                            {transaction.type === 'purchase' ? '购买' : 
                             transaction.type === 'usage' ? '使用' : '奖励'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">积分说明</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• 每生成1张图片消耗1个积分</li>
                <li>• 新用户注册可获得免费积分</li>
                <li>• 可使用兑换码获得免费积分</li>
                <li>• 积分可用于所有AI图片生成功能</li>
                <li>• 更多功能正在开发中...</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({ ...paymentModal, isOpen: false })}
        credits={paymentModal.credits}
        price={paymentModal.price}
        clientSecret={paymentModal.clientSecret}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  )
}